import { BatchRecordWrite, BatchWriteFailure, MAX_BATCH_SIZE } from './batching';

/** Progress for an in flight bulk run, including an ETA measured from real throughput. */
export interface BulkRunProgress {
	completed: number;
	total: number;
	elapsedMs: number;
	etaMs: number | null;
}

export interface BulkRunResult {
	succeeded: string[];
	failed: BatchWriteFailure[];
	cancelled: boolean;
	elapsedMs: number;
}

export interface BulkRunner {
	writeRecordsBulk: (
		writes: BatchRecordWrite[],
		options: {
			onProgress?: (completed: number) => void;
			isCancelled?: () => boolean;
			chunkSize?: number;
			concurrency?: number;
			onChunkStart?: (ids: string[]) => void;
			onChunkRetry?: (ids: string[], attempt: number) => void;
			onChunkSettled?: (succeeded: string[], failed: BatchWriteFailure[]) => void;
		}
	) => Promise<{ succeeded: string[]; failed: BatchWriteFailure[] }>;
}

export interface BulkRunOptions {
	onProgress?: (progress: BulkRunProgress) => void;
	isCancelled?: () => boolean;
	chunkSize?: number;
	concurrency?: number;
	/** Per record status, so the preview can show what is pending, writing, done or failed. */
	onChunkStart?: (ids: string[]) => void;
	onChunkRetry?: (ids: string[], attempt: number) => void;
	onChunkSettled?: (succeeded: string[], failed: BatchWriteFailure[]) => void;
}

/**
 * Runs a set of writes through the model's batched path, reporting progress and an ETA.
 *
 * The ETA is derived from the throughput observed so far rather than a fixed guess, so it
 * settles quickly and stays honest when the connection is slow.
 */
export async function runBulkWrites(runner: BulkRunner, writes: BatchRecordWrite[], options: BulkRunOptions = {}): Promise<BulkRunResult> {
	let total = writes.length;
	let startedAt = Date.now();

	if (!total) {
		return { succeeded: [], failed: [], cancelled: false, elapsedMs: 0 };
	}

	options.onProgress?.({ completed: 0, total, elapsedMs: 0, etaMs: null });

	let result = await runner.writeRecordsBulk(writes, {
		chunkSize: Math.min(options.chunkSize ?? MAX_BATCH_SIZE, MAX_BATCH_SIZE),
		concurrency: options.concurrency,
		isCancelled: options.isCancelled,
		onChunkStart: options.onChunkStart,
		onChunkRetry: options.onChunkRetry,
		onChunkSettled: options.onChunkSettled,
		onProgress: (completed) => {
			let elapsedMs = Date.now() - startedAt;
			let etaMs: number | null = null;

			if (completed > 0 && elapsedMs > 0 && completed < total) {
				etaMs = Math.round((elapsedMs / completed) * (total - completed));
			} else if (completed >= total) {
				etaMs = 0;
			}

			options.onProgress?.({ completed, total, elapsedMs, etaMs });
		},
	});

	let elapsedMs = Date.now() - startedAt;
	let processed = result.succeeded.length + result.failed.length;

	options.onProgress?.({ completed: processed, total, elapsedMs, etaMs: 0 });

	return {
		succeeded: result.succeeded,
		failed: result.failed,
		cancelled: processed < total,
		elapsedMs,
	};
}
